import { gql } from '@apollo/client';
import { Box, Button, Typography } from '@mui/material';
import { PlotRoutes } from 'Routes';
import { PRIVACY_POLICY_LINK, TERMS_OF_SERVICE_LINK } from 'constants/support';
import { useGenerateAuthUrlQuery } from 'graphql/generated';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { theme } from 'styles/theme';
import { StyledForgotPasswordLink } from './styles';

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
gql`
  query GenerateAuthURL {
    workosAuthUrl {
      loginUrl
      signupUrl
    }
  }
`;

export const WorkosAuth = () => {
  const [searchParams] = useSearchParams();
  const page = searchParams.get('page') as 'login' | 'signup' | 'forgot' | null;
  const { data } = useGenerateAuthUrlQuery();

  useEffect(() => {
    if (page === 'signup' && data?.workosAuthUrl.signupUrl) {
      window.open(data?.workosAuthUrl.signupUrl || '', '_self');
    }

    if (page === 'login' && data?.workosAuthUrl.loginUrl) {
      window.open(data?.workosAuthUrl.loginUrl || '', '_self');
    }

    if (page === 'forgot') {
      window.open(PlotRoutes.forgotPassword(), '_self');
    }
  }, [data, page]);

  if (page === 'signup' || page === 'login') {
    return null;
  }

  if (
    process.env.REACT_APP_DISABLE_AUTHENTICATION &&
    process.env.REACT_APP_DISABLE_AUTHENTICATION.toLowerCase() === 'true'
  ) {
    return (
      <Typography
        variant="headline-md"
        sx={{ color: theme.colors?.primary.white }}
      >
        We are making some updates... <br />
        Please check back later!
      </Typography>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={2}
      justifyContent="center"
      width="100%"
    >
      <Box width="100%" display="flex" justifyContent="flex-start" mb={2}>
        <Box component="img" src="/logo-plot.png" />
      </Box>
      <Typography
        width="100%"
        display="block"
        variant="headline-xl"
        letterSpacing="-1px"
        lineHeight={theme.spacing(14)}
        sx={{
          color: theme.colors?.primary.parchment,
          fontSize: theme.spacing(10),
        }}
      >
        Jam on content <br /> wherever you are.
      </Typography>
      <Typography
        variant="body-xl"
        width="100%"
        display="block"
        sx={{
          color: theme.colors?.primary.parchment,
          fontWeight: 500,
        }}
      >
        Store, collaborate, and organize content in Plot.
      </Typography>

      <Box
        component="a"
        href={data?.workosAuthUrl.signupUrl || ''}
        target="_self"
        width="100%"
      >
        <Button
          type="submit"
          fullWidth
          variant="secondary"
          size="medium"
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 2,
            backgroundColor: theme.colors?.primary.parchment,
            color: theme.colors?.primary.black,
            border: 'none',
            '&:hover': {
              backgroundColor: theme.colors?.primary.parchment,
              color: theme.colors?.primary.black,
              border: 'none',
            },
          }}
        >
          Create an account
        </Button>
      </Box>

      <Box
        component="a"
        href={data?.workosAuthUrl.loginUrl || ''}
        target="_self"
        width="100%"
      >
        <Button
          type="submit"
          fullWidth
          variant="primary"
          size="medium"
          sx={{
            mt: 2,
            p: 3,
            borderRadius: 2,
            backgroundColor: 'rgba(250, 243, 236, 0.15)',
            color: theme.colors?.primary.parchment,
            border: 'none',
            '&:hover': {
              backgroundColor: 'rgba(250, 243, 236, 0.15)',
              color: theme.colors?.primary.parchment,
              border: 'none',
            },
          }}
        >
          Login
        </Button>
      </Box>

      <Typography variant="body-xl" color={theme.colors?.utility[600]} mt={2}>
        By signing up, you agree to our{' '}
        <Box
          component="a"
          href={TERMS_OF_SERVICE_LINK}
          target="_blank"
          sx={{
            textDecoration: 'underline !important',
            color: `${theme.colors?.primary.parchment} !important`,
          }}
        >
          Term of Service
        </Box>{' '}
        and{' '}
        <Box
          component="a"
          href={PRIVACY_POLICY_LINK}
          target="_blank"
          sx={{
            textDecoration: 'underline !important',
            color: `${theme.colors?.primary.parchment} !important`,
          }}
        >
          Privacy Policy
        </Box>
        .
      </Typography>

      <StyledForgotPasswordLink
        to={{
          pathname: '/auth/forgot',
        }}
      >
        <Typography className="forgot-link" variant="headline-md">
          Forgot password?
        </Typography>
      </StyledForgotPasswordLink>
    </Box>
  );
};
